import React, { type CSSProperties } from 'react';
import {
    Archive,
    ArrowRight,
    BadgeCheck,
    Building2,
    CalendarDays,
    CheckCircle2,
    DoorOpen,
    Hammer,
    House,
    Layers3,
    MapPin,
    Palette,
    PanelsTopLeft,
    Phone,
    Ruler,
    Sparkles,
    Users,
    Waves,
} from 'lucide-react';
import GoogleServiceArea from '../../external/google-service-area/GoogleServiceArea';
import {
    serviceAreaIconTokens,
    serviceAreaTokens,
    type ServiceAreaColorToken,
    type ServiceAreaIconToken,
} from './ServiceArea.tokens';

export type ServiceAreaLinkComponent = React.ComponentType<{
    href: string;
    children?: React.ReactNode;
    className?: string;
    style?: CSSProperties;
    title?: string;
    target?: string;
    rel?: string;
    'aria-label'?: string;
    [key: string]: any;
}>;

export type ServiceAreaIcon = ServiceAreaIconToken | React.ReactNode | React.ElementType | false;

export type ServiceAreaImageSource =
    | string
    | {
          src?: string;
          width?: number;
          height?: number;
          [key: string]: unknown;
      };

export type ServiceAreaImage = {
    src?: ServiceAreaImageSource;
    url?: string;
    original?: string;
    thumbnail?: string;
    alt?: string;
    title?: string;
    width?: number;
    height?: number;
    objectPosition?: string;
    loading?: 'eager' | 'lazy';
    decoding?: 'async' | 'auto' | 'sync';
    decorative?: boolean;
};

export type ServiceAreaCatalogueEntry = {
    id?: string;
    label?: React.ReactNode;
    title?: React.ReactNode;
    name?: React.ReactNode;
    description?: React.ReactNode;
    href?: string;
    image?: ServiceAreaImage | string | false;
    icon?: ServiceAreaIcon;
    meta?: React.ReactNode;
};

export type ServiceAreaCatalogue = {
    solutions?: Record<string, ServiceAreaCatalogueEntry>;
    finishes?: Record<string, ServiceAreaCatalogueEntry>;
    features?: Record<string, ServiceAreaCatalogueEntry>;
};

export type ServiceAreaLocation = {
    suburb: string;
    state: string;
    postcode: string;
    localGovernmentArea?: string;
    region?: string;
    areaTags?: string[];
};

export type ServiceAreaDwellingStructurePercent = {
    separateHouse?: number;
    semiDetachedRowTerraceTownhouse?: number;
    flatOrApartment?: number;
    otherDwelling?: number;
};

export type ServiceAreaDwellingProfile = {
    dataYear?: number;
    dwellingStructurePercent?: ServiceAreaDwellingStructurePercent;
    primaryPattern?: string;
    sourceIds?: string[];
};

export type ServiceAreaPage = {
    status?: string;
    pageType?: string;
    location: ServiceAreaLocation;
    areaProfile?: {
        dwellingProfile?: ServiceAreaDwellingProfile;
    };
    recommendations?: {
        solutionIds?: string[];
        finishIds?: string[];
        featureIds?: string[];
    };
};

export type ServiceAreaCollection = {
    $schema?: string;
    schemaVersion?: string;
    contentType?: string;
    locale?: string;
    catalogue?: ServiceAreaCatalogue;
    pages: Record<string, ServiceAreaPage>;
    sources?: Record<string, unknown>;
};

export type ServiceAreaAction = {
    label: React.ReactNode;
    href: string;
    ariaLabel?: string;
    title?: string;
    target?: string;
    rel?: string;
    variant?: 'primary' | 'secondary';
};

export type ServiceAreaHeroBadge = {
    id?: string | number;
    title: React.ReactNode;
    description?: React.ReactNode;
    icon?: ServiceAreaIcon;
    position?: 'top' | 'bottom';
};

export type ServiceAreaStat = {
    id?: string | number;
    label: React.ReactNode;
    value: React.ReactNode;
    description?: React.ReactNode;
    icon?: ServiceAreaIcon;
};

export type ServiceAreaInsight = {
    id?: string | number;
    text: React.ReactNode;
    icon?: ServiceAreaIcon;
};

export type ServiceAreaMap = {
    image?: ServiceAreaImage | string | false;
    embedUrl?: string;
    title?: string;
    serviceLabel?: React.ReactNode;
    detailImage?: ServiceAreaImage | string | false;
};

export type ServiceAreaFeature = {
    id?: string | number;
    title: React.ReactNode;
    description?: React.ReactNode;
    icon?: ServiceAreaIcon;
};

export type ServiceAreaProcessStep = {
    id?: string | number;
    title: React.ReactNode;
    description?: React.ReactNode;
    icon?: ServiceAreaIcon;
};

export type ServiceAreaNearbyArea = {
    id?: string | number;
    label: React.ReactNode;
    href?: string;
    ariaLabel?: string;
};

export type ServiceAreaCta = {
    title: React.ReactNode;
    description?: React.ReactNode;
    image?: ServiceAreaImage | string | false;
    primaryAction: ServiceAreaAction;
    secondaryAction?: ServiceAreaAction | false;
};

export type ServiceAreaLabels = {
    insightsEyebrow?: React.ReactNode;
    insightsTitle?: React.ReactNode;
    solutionsTitle?: React.ReactNode;
    finishesTitle?: React.ReactNode;
    fitTitle?: React.ReactNode;
    processTitle?: React.ReactNode;
    nearbyAreasTitle?: React.ReactNode;
};

export type ServiceAreaColors = Partial<Record<ServiceAreaColorToken, string>>;

export type ServiceAreaProps = {
    data: ServiceAreaCollection;
    pagePath?: string;
    serviceName?: string;
    eyebrow?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    heroImage?: ServiceAreaImage | string | false;
    heroBadges?: ServiceAreaHeroBadge[] | false;
    primaryAction?: ServiceAreaAction | false;
    secondaryAction?: ServiceAreaAction | false;
    stats?: ServiceAreaStat[] | false;
    insights?: ServiceAreaInsight[] | false;
    map?: ServiceAreaMap | false;
    solutions?: ServiceAreaCatalogueEntry[] | false;
    finishes?: ServiceAreaCatalogueEntry[] | false;
    fitFeatures?: ServiceAreaFeature[] | false;
    process?: ServiceAreaProcessStep[] | false;
    nearbyAreas?: ServiceAreaNearbyArea[] | false;
    cta?: ServiceAreaCta | false;
    labels?: ServiceAreaLabels;
    LinkComponent?: ServiceAreaLinkComponent;
    Link?: ServiceAreaLinkComponent;
    headingLevel?: 'h1' | 'h2';
    accentColor?: string;
    accentColour?: string;
    accentDarkColor?: string;
    accentDarkColour?: string;
    accentSoftColor?: string;
    accentSoftColour?: string;
    actionColor?: string;
    actionColour?: string;
    actionDarkColor?: string;
    actionDarkColour?: string;
    backgroundColor?: string;
    backgroundColour?: string;
    surfaceColor?: string;
    surfaceColour?: string;
    textColor?: string;
    textColour?: string;
    textSoftColor?: string;
    textSoftColour?: string;
    colors?: ServiceAreaColors;
    colours?: ServiceAreaColors;
    fontFamily?: string;
    maxWidth?: number | string;
    radius?: number | string;
    id?: string;
    className?: string;
    style?: CSSProperties;
    ariaLabel?: string;
};

type ServiceAreaCssVars = CSSProperties &
    Record<`--service-area-${string}`, string | number | undefined>;

type NormalizedImage = {
    src: string;
    alt: string;
    title?: string;
    width?: number;
    height?: number;
    objectPosition?: string;
    loading: 'eager' | 'lazy';
    decoding: 'async' | 'auto' | 'sync';
    decorative: boolean;
};

type ResolvedCatalogueEntry = ServiceAreaCatalogueEntry & {
    id: string;
    label: React.ReactNode;
};

const t = serviceAreaTokens;
const iconTokenSet = new Set<string>(serviceAreaIconTokens);

const iconRegistry: Record<ServiceAreaIconToken, React.ElementType> = {
    arrowRight: ArrowRight,
    badgeCheck: BadgeCheck,
    building: Building2,
    calendar: CalendarDays,
    checkCircle: CheckCircle2,
    drawers: Archive,
    house: House,
    layers: Layers3,
    mapPin: MapPin,
    palette: Palette,
    phone: Phone,
    ruler: Ruler,
    sliding: PanelsTopLeft,
    sparkles: Sparkles,
    tools: Hammer,
    users: Users,
    wardrobe: DoorOpen,
    waves: Waves,
};

const serviceAreaCss = `
.service-area {
  --service-area-bg: ${t.colors.background};
  --service-area-surface: ${t.colors.surface};
  --service-area-surface-soft: ${t.colors.surfaceSoft};
  --service-area-text: ${t.colors.text};
  --service-area-text-soft: ${t.colors.textSoft};
  --service-area-text-muted: ${t.colors.textMuted};
  --service-area-accent: ${t.colors.accent};
  --service-area-accent-dark: ${t.colors.accentDark};
  --service-area-accent-soft: ${t.colors.accentSoft};
  --service-area-accent-contrast: ${t.colors.accentContrast};
  --service-area-action: ${t.colors.action};
  --service-area-action-dark: ${t.colors.actionDark};
  --service-area-action-contrast: ${t.colors.actionContrast};
  --service-area-border: ${t.colors.border};
  --service-area-border-strong: ${t.colors.borderStrong};
  --service-area-success: ${t.colors.success};
  --service-area-map-bg: ${t.colors.mapBackground};
  --service-area-cta-bg: ${t.colors.ctaBackground};
  --service-area-cta-text: ${t.colors.ctaText};
  --service-area-shadow: ${t.colors.shadow};
  --service-area-soft-shadow: ${t.colors.softShadow};
  --service-area-font: ${t.typography.fontFamily};
  --service-area-max-width: ${t.sizes.maxWidth}px;
  --service-area-radius: ${t.radius.card}px;
  width: 100%;
  min-width: 0;
  padding: ${t.spacing.sectionY}px ${t.spacing.sectionX}px;
  background: var(--service-area-bg);
  color: var(--service-area-text);
  font-family: var(--service-area-font);
  box-sizing: border-box;
  container-type: inline-size;
}
.service-area,
.service-area * {
  box-sizing: border-box;
}
.service-area-shell {
  width: 100%;
  max-width: var(--service-area-max-width);
  margin: 0 auto;
}
.service-area-hero {
  display: grid;
  grid-template-columns: minmax(0, .82fr) minmax(480px, 1.18fr);
  gap: 34px;
  align-items: center;
}
.service-area-hero-copy {
  min-width: 0;
  padding: 18px 0;
}
.service-area-eyebrow,
.service-area-section-eyebrow {
  display: inline-flex;
  flex-direction: column;
  gap: 9px;
  margin: 0 0 16px;
  color: var(--service-area-accent);
  font-size: 13px;
  line-height: 1.15;
  font-weight: 900;
  letter-spacing: .02em;
  text-transform: uppercase;
}
.service-area-eyebrow::after,
.service-area-section-eyebrow::after {
  content: "";
  width: 36px;
  height: 3px;
  border-radius: 999px;
  background: currentColor;
}
.service-area-title {
  max-width: 570px;
  margin: 0;
  color: var(--service-area-text);
  font-size: clamp(${t.typography.title.mobileFontSize}px, 4.1cqw, ${t.typography.title.fontSize}px);
  line-height: ${t.typography.title.lineHeight};
  font-weight: ${t.typography.title.fontWeight};
  letter-spacing: -.035em;
  text-wrap: balance;
  overflow-wrap: anywhere;
}
.service-area-description {
  max-width: 570px;
  margin: 20px 0 0;
  color: var(--service-area-text-soft);
  font-size: ${t.typography.body.fontSize}px;
  line-height: ${t.typography.body.lineHeight};
  font-weight: ${t.typography.body.fontWeight};
}
.service-area-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 22px;
}
.service-area-action {
  display: inline-flex;
  min-height: 46px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 1px solid var(--service-area-border-strong);
  border-radius: ${t.radius.button}px;
  padding: 11px 18px;
  font-size: 14px;
  line-height: 1;
  font-weight: 850;
  text-decoration: none;
  transition: transform 160ms ease, background-color 160ms ease, border-color 160ms ease;
}
.service-area-action:hover {
  transform: translateY(-1px);
}
.service-area-action:focus-visible,
.service-area-card-link:focus-visible,
.service-area-nearby-link:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--service-area-accent) 28%, transparent);
  outline-offset: 3px;
}
.service-area-action--primary {
  border-color: var(--service-area-action);
  background: var(--service-area-action);
  color: var(--service-area-action-contrast);
  box-shadow: 0 12px 24px color-mix(in srgb, var(--service-area-action) 22%, transparent);
}
.service-area-action--primary:hover {
  border-color: var(--service-area-action-dark);
  background: var(--service-area-action-dark);
}
.service-area-action--secondary {
  background: var(--service-area-surface);
  color: var(--service-area-text);
}
.service-area-action--secondary:hover {
  border-color: var(--service-area-accent);
  color: var(--service-area-accent-dark);
}
.service-area-hero-media {
  position: relative;
  min-width: 0;
  min-height: ${t.sizes.heroMediaMinHeight}px;
  overflow: hidden;
  border: 1px solid var(--service-area-border);
  border-radius: var(--service-area-radius);
  background:
    linear-gradient(135deg, var(--service-area-accent-soft), var(--service-area-surface-soft));
  box-shadow: var(--service-area-shadow);
}
.service-area-hero-media > img {
  display: block;
  width: 100%;
  height: 100%;
  min-height: ${t.sizes.heroMediaMinHeight}px;
  object-fit: cover;
}
.service-area-media-fallback {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 32px;
  color: var(--service-area-accent);
  text-align: center;
}
.service-area-media-fallback::before {
  content: "";
  position: absolute;
  inset: 11% 8%;
  border: 2px solid color-mix(in srgb, var(--service-area-accent) 18%, transparent);
  border-radius: calc(var(--service-area-radius) * 1.3);
  background:
    linear-gradient(90deg, transparent 49.5%, color-mix(in srgb, var(--service-area-accent) 14%, transparent) 50%, transparent 50.5%),
    linear-gradient(180deg, rgba(255,255,255,.76), rgba(255,255,255,.26));
}
.service-area-media-fallback-content {
  position: relative;
  z-index: 1;
  display: grid;
  justify-items: center;
  gap: 12px;
  font-weight: 850;
}
.service-area-hero-badge {
  position: absolute;
  right: 18px;
  z-index: 2;
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  gap: 10px;
  align-items: center;
  max-width: min(230px, calc(100% - 36px));
  padding: 12px 14px;
  border: 1px solid var(--service-area-border);
  border-radius: var(--service-area-radius);
  background: color-mix(in srgb, var(--service-area-surface) 94%, transparent);
  box-shadow: var(--service-area-soft-shadow);
  backdrop-filter: blur(8px);
}
.service-area-hero-badge--top { top: 18px; }
.service-area-hero-badge--bottom { bottom: 18px; }
.service-area-hero-badge-icon,
.service-area-stat-icon,
.service-area-feature-icon,
.service-area-step-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--service-area-accent);
  background: var(--service-area-accent-soft);
}
.service-area-hero-badge-icon {
  width: 36px;
  height: 36px;
  border-radius: 999px;
}
.service-area-hero-badge-title {
  margin: 0;
  color: var(--service-area-text);
  font-size: 13px;
  line-height: 1.25;
  font-weight: 850;
}
.service-area-hero-badge-description {
  margin: 3px 0 0;
  color: var(--service-area-text-muted);
  font-size: 11px;
  line-height: 1.25;
  font-weight: 650;
}
.service-area-stats {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  margin: 30px 0 0;
  padding: 0;
  overflow: hidden;
  border: 1px solid var(--service-area-border);
  border-radius: var(--service-area-radius);
  background: var(--service-area-surface);
  list-style: none;
}
.service-area-stat {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 11px;
  align-items: center;
  min-width: 0;
  min-height: 100px;
  padding: 16px;
  border-right: 1px solid var(--service-area-border);
}
.service-area-stat:last-child { border-right: 0; }
.service-area-stat-icon {
  width: 42px;
  height: 42px;
  border-radius: 999px;
}
.service-area-stat-copy {
  min-width: 0;
}
.service-area-stat-label,
.service-area-stat-value,
.service-area-stat-description {
  margin: 0;
}
.service-area-stat-label {
  color: var(--service-area-text-soft);
  font-size: 11px;
  line-height: 1.2;
  font-weight: 750;
}
.service-area-stat-value {
  margin-top: 2px;
  color: var(--service-area-text);
  font-size: 18px;
  line-height: 1.1;
  font-weight: 900;
  overflow-wrap: normal;
  word-break: normal;
  hyphens: none;
}
.service-area-stat-description {
  margin-top: 4px;
  color: var(--service-area-text-muted);
  font-size: 10px;
  line-height: 1.3;
  font-weight: 600;
}
.service-area-insights {
  display: grid;
  grid-template-columns: minmax(280px, .68fr) minmax(0, 1.42fr);
  gap: 28px;
  align-items: stretch;
  margin-top: 30px;
}
.service-area-insight-copy {
  min-width: 0;
  padding: 14px 0 0;
}
.service-area-section-title {
  margin: 0;
  color: var(--service-area-text);
  font-size: ${t.typography.sectionTitle.fontSize}px;
  line-height: ${t.typography.sectionTitle.lineHeight};
  font-weight: ${t.typography.sectionTitle.fontWeight};
  letter-spacing: -.02em;
  text-wrap: balance;
}
.service-area-insight-list {
  display: grid;
  gap: 11px;
  margin: 20px 0 0;
  padding: 0;
  list-style: none;
}
.service-area-insight-item {
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  gap: 9px;
  color: var(--service-area-text-soft);
  font-size: 14px;
  line-height: 1.45;
  font-weight: 600;
}
.service-area-insight-item svg { color: var(--service-area-accent); }
.service-area-map {
  position: relative;
  min-width: 0;
  min-height: ${t.sizes.mapMinHeight}px;
  overflow: hidden;
  border: 1px solid var(--service-area-border);
  border-radius: var(--service-area-radius);
  background: var(--service-area-map-bg);
}
.service-area-map-image,
.service-area-map-frame,
.service-area-map-aspect,
.service-area-map-iframe {
  width: 100%;
  height: 100%;
}
.service-area-map-image {
  display: block;
  min-height: ${t.sizes.mapMinHeight}px;
  object-fit: cover;
}
.service-area-map-frame,
.service-area-map-aspect {
  position: absolute;
  inset: 0;
  max-width: none;
  padding: 0;
}
.service-area-map-iframe {
  position: absolute;
  inset: 0;
}
.service-area-map-fallback {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  overflow: hidden;
  background:
    linear-gradient(90deg, color-mix(in srgb, var(--service-area-accent) 7%, transparent) 1px, transparent 1px),
    linear-gradient(0deg, color-mix(in srgb, var(--service-area-accent) 7%, transparent) 1px, transparent 1px),
    radial-gradient(circle at 72% 22%, rgba(116, 202, 255, .55), transparent 26%),
    radial-gradient(circle at 24% 36%, rgba(152, 220, 177, .58), transparent 30%),
    var(--service-area-map-bg);
  background-size: 42px 42px, 42px 42px, auto, auto, auto;
}
.service-area-map-fallback::before {
  content: "";
  position: absolute;
  width: 56%;
  height: 63%;
  border: 3px solid var(--service-area-accent);
  border-radius: 42% 53% 38% 49%;
  background: color-mix(in srgb, var(--service-area-accent) 12%, transparent);
  transform: rotate(-7deg);
}
.service-area-map-fallback-content {
  position: relative;
  z-index: 1;
  display: grid;
  justify-items: center;
  gap: 8px;
  padding: 20px;
  border-radius: var(--service-area-radius);
  background: color-mix(in srgb, var(--service-area-surface) 90%, transparent);
  color: var(--service-area-text);
  box-shadow: var(--service-area-soft-shadow);
  text-align: center;
}
.service-area-map-fallback-content svg { color: var(--service-area-accent); }
.service-area-map-fallback-content strong { font-size: 18px; }
.service-area-map-fallback-content span {
  color: var(--service-area-text-muted);
  font-size: 12px;
}
.service-area-map-label {
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: calc(100% - 28px);
  padding: 9px 12px;
  border-radius: ${t.radius.button}px;
  background: var(--service-area-text);
  color: #ffffff;
  font-size: 12px;
  line-height: 1.25;
  font-weight: 800;
  box-shadow: var(--service-area-soft-shadow);
}
.service-area-map-detail {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 3;
  width: min(255px, calc(100% - 28px));
  padding: 14px;
  border: 1px solid var(--service-area-border);
  border-radius: var(--service-area-radius);
  background: color-mix(in srgb, var(--service-area-surface) 94%, transparent);
  box-shadow: var(--service-area-soft-shadow);
  backdrop-filter: blur(8px);
}
.service-area-map-detail-title {
  margin: 0;
  color: var(--service-area-text);
  font-size: 13px;
  line-height: 1.3;
  font-weight: 900;
}
.service-area-map-detail-list {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 5px 8px;
  margin: 9px 0 0;
  font-size: 11px;
  line-height: 1.3;
}
.service-area-map-detail-list dt { color: var(--service-area-text-muted); font-weight: 700; }
.service-area-map-detail-list dd { margin: 0; color: var(--service-area-text-soft); font-weight: 750; }
.service-area-map-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
}
.service-area-map-tags li {
  padding: 5px 8px;
  border-radius: 999px;
  background: var(--service-area-accent-soft);
  color: var(--service-area-accent-dark);
  font-size: 10px;
  line-height: 1;
  font-weight: 750;
}
.service-area-map-thumbnail {
  position: absolute;
  right: 14px;
  bottom: 14px;
  z-index: 3;
  display: block;
  width: min(235px, 36%);
  aspect-ratio: 1.28 / 1;
  overflow: hidden;
  border: 3px solid var(--service-area-surface);
  border-radius: var(--service-area-radius);
  background: var(--service-area-surface-soft);
  box-shadow: var(--service-area-soft-shadow);
}
.service-area-map-thumbnail img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.service-area-section {
  margin-top: 28px;
}
.service-area-section-heading {
  display: flex;
  align-items: center;
  gap: 18px;
  margin: 0 0 14px;
  text-align: center;
}
.service-area-section-heading::before,
.service-area-section-heading::after {
  content: "";
  flex: 1 1 auto;
  height: 1px;
  background: var(--service-area-border);
}
.service-area-section-heading .service-area-section-title { flex: 0 1 auto; }
.service-area-card-grid {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: var(--service-area-card-width, calc((100% - 60px) / 6));
  gap: 12px;
  margin: 0;
  padding: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  border: 1px solid var(--service-area-border);
  border-radius: var(--service-area-radius);
  background: var(--service-area-surface);
  list-style: none;
  scroll-behavior: smooth;
  scroll-snap-type: x proximity;
  scrollbar-width: thin;
  scrollbar-color: var(--service-area-accent) var(--service-area-accent-soft);
  -webkit-overflow-scrolling: touch;
}
.service-area-card-grid:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--service-area-accent) 28%, transparent);
  outline-offset: 3px;
}
.service-area-card-grid::-webkit-scrollbar {
  height: 8px;
}
.service-area-card-grid::-webkit-scrollbar-track {
  border-radius: 999px;
  background: var(--service-area-accent-soft);
}
.service-area-card-grid::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: var(--service-area-accent);
}
.service-area-card {
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--service-area-border);
  border-radius: var(--service-area-radius);
  background: var(--service-area-surface);
  scroll-snap-align: start;
}
.service-area-card-link,
.service-area-card-static {
  display: block;
  height: 100%;
  color: inherit;
  text-decoration: none;
}
.service-area-card-media {
  position: relative;
  display: grid;
  height: 140px;
  place-items: center;
  overflow: hidden;
  background: var(--service-area-surface-soft);
  color: var(--service-area-accent);
}
.service-area-card--finish .service-area-card-media { height: 180px; }
.service-area-card-media img {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 240ms ease;
}
.service-area-card-link:hover .service-area-card-media img { transform: scale(1.025); }
.service-area-card-copy {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  gap: 9px;
  align-items: start;
  min-height: 70px;
  padding: 12px;
}
.service-area-card-icon { color: var(--service-area-accent); }
.service-area-card-title,
.service-area-card-description,
.service-area-card-meta { margin: 0; }
.service-area-card-title {
  color: var(--service-area-text);
  font-size: 13px;
  line-height: 1.25;
  font-weight: 850;
  overflow-wrap: anywhere;
}
.service-area-card-description,
.service-area-card-meta {
  margin-top: 5px;
  color: var(--service-area-text-muted);
  font-size: 11px;
  line-height: 1.35;
  font-weight: 600;
}
.service-area-card--finish .service-area-card-copy {
  display: block;
  min-height: 58px;
}
.service-area-feature-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 0;
  padding: 0;
  overflow: hidden;
  border: 1px solid var(--service-area-border);
  border-radius: var(--service-area-radius);
  background: var(--service-area-surface);
  list-style: none;
}
.service-area-feature {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  gap: 13px;
  min-width: 0;
  padding: 20px;
  border-right: 1px solid var(--service-area-border);
}
.service-area-feature:last-child { border-right: 0; }
.service-area-feature-icon,
.service-area-step-icon {
  width: 48px;
  height: 48px;
  border-radius: 999px;
}
.service-area-feature-title,
.service-area-feature-description,
.service-area-step-title,
.service-area-step-description {
  margin: 0;
}
.service-area-feature-title,
.service-area-step-title {
  color: var(--service-area-text);
  font-size: 13px;
  line-height: 1.25;
  font-weight: 850;
}
.service-area-feature-description,
.service-area-step-description {
  margin-top: 7px;
  color: var(--service-area-text-muted);
  font-size: 11px;
  line-height: 1.45;
  font-weight: 600;
}
.service-area-process {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 46px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.service-area-step {
  position: relative;
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr);
  gap: 14px;
  min-width: 0;
  padding: 18px;
  border: 1px solid var(--service-area-border);
  border-radius: var(--service-area-radius);
  background: var(--service-area-surface);
}
.service-area-step:not(:last-child)::after {
  content: "→";
  position: absolute;
  top: 50%;
  right: -34px;
  color: var(--service-area-accent);
  font-size: 25px;
  line-height: 1;
  transform: translateY(-50%);
}
.service-area-step-icon-wrap { position: relative; }
.service-area-step-number {
  position: absolute;
  top: -7px;
  right: -3px;
  display: grid;
  width: 22px;
  height: 22px;
  place-items: center;
  border: 2px solid var(--service-area-surface);
  border-radius: 999px;
  background: var(--service-area-accent-soft);
  color: var(--service-area-accent);
  font-size: 10px;
  font-weight: 900;
}
.service-area-nearby {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.service-area-nearby-link,
.service-area-nearby-static {
  display: inline-flex;
  min-height: 30px;
  align-items: center;
  gap: 7px;
  border: 1px solid var(--service-area-border);
  border-radius: 999px;
  padding: 6px 12px;
  background: var(--service-area-surface);
  color: var(--service-area-text-soft);
  font-size: 11px;
  line-height: 1;
  font-weight: 700;
  text-decoration: none;
}
.service-area-nearby-link svg,
.service-area-nearby-static svg { color: var(--service-area-accent); }
.service-area-cta {
  position: relative;
  isolation: isolate;
  display: grid;
  grid-template-columns: minmax(0, .85fr) minmax(360px, 1.15fr);
  min-height: 250px;
  align-items: center;
  margin-top: 18px;
  overflow: hidden;
  border-radius: var(--service-area-radius);
  background: var(--service-area-cta-bg);
  color: var(--service-area-cta-text);
}
.service-area-cta::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  background: linear-gradient(90deg, var(--service-area-cta-bg) 0%, color-mix(in srgb, var(--service-area-cta-bg) 96%, transparent) 42%, transparent 74%);
}
.service-area-cta-copy {
  z-index: 2;
  min-width: 0;
  padding: 34px;
}
.service-area-cta-title {
  max-width: 520px;
  margin: 0;
  color: var(--service-area-cta-text);
  font-size: 30px;
  line-height: 1.1;
  font-weight: 900;
  letter-spacing: -.025em;
  text-wrap: balance;
}
.service-area-cta-description {
  max-width: 500px;
  margin: 10px 0 0;
  color: color-mix(in srgb, var(--service-area-cta-text) 82%, transparent);
  font-size: 14px;
  line-height: 1.5;
  font-weight: 600;
}
.service-area-cta .service-area-actions { margin-top: 20px; }
.service-area-cta .service-area-action--secondary {
  border-color: color-mix(in srgb, var(--service-area-cta-text) 46%, transparent);
  background: var(--service-area-cta-text);
  color: var(--service-area-cta-bg);
}
.service-area-cta-media {
  position: absolute;
  inset: 0 0 0 38%;
  z-index: -2;
}
.service-area-cta-media img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
@media (prefers-reduced-motion: reduce) {
  .service-area-action,
  .service-area-card-media img {
    transition: none;
  }
  .service-area-action:hover,
  .service-area-card-link:hover .service-area-card-media img {
    transform: none;
  }
}
@container (max-width: 1180px) {
  .service-area-stats { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .service-area-stat:nth-child(3n) { border-right: 0; }
  .service-area-stat:nth-child(-n + 3) { border-bottom: 1px solid var(--service-area-border); }
}
@container (max-width: 1040px) {
  .service-area-hero { grid-template-columns: minmax(0, 1fr); }
  .service-area-hero-copy { padding-bottom: 0; }
  .service-area-title { max-width: 760px; font-size: ${t.typography.title.tabletFontSize}px; }
  .service-area-description { max-width: 720px; }
  .service-area-insights { grid-template-columns: minmax(0, 1fr); }
  .service-area-insight-copy { display: grid; grid-template-columns: minmax(0, 1fr) minmax(280px, .72fr); gap: 24px; }
  .service-area-card-grid { --service-area-card-width: calc((100% - 24px) / 3) !important; }
  .service-area-feature-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .service-area-feature:nth-child(2) { border-right: 0; }
  .service-area-feature:nth-child(-n + 2) { border-bottom: 1px solid var(--service-area-border); }
}
@container (max-width: 720px) {
  .service-area { padding: 22px 14px; }
  .service-area-hero { gap: 22px; }
  .service-area-title { font-size: ${t.typography.title.mobileFontSize}px; }
  .service-area-description { margin-top: 15px; font-size: ${t.typography.body.mobileFontSize}px; }
  .service-area-hero-media,
  .service-area-hero-media > img { min-height: 330px; }
  .service-area-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .service-area-stat,
  .service-area-stat:nth-child(3n) { border-right: 1px solid var(--service-area-border); }
  .service-area-stat:nth-child(even) { border-right: 0; }
  .service-area-stat:nth-child(-n + 4) { border-bottom: 1px solid var(--service-area-border); }
  .service-area-insight-copy { display: block; }
  .service-area-map,
  .service-area-map-image { min-height: 460px; }
  .service-area-map-detail { top: auto; bottom: 14px; left: 14px; right: 14px; width: auto; }
  .service-area-map-thumbnail { display: none; }
  .service-area-section-heading { gap: 10px; }
  .service-area-section-title { font-size: ${t.typography.sectionTitle.mobileFontSize}px; }
  .service-area-card-grid { --service-area-card-width: calc((100% - 9px) / 2) !important; gap: 9px; padding: 9px; }
  .service-area-card-media { height: 120px; }
  .service-area-card--finish .service-area-card-media { height: 145px; }
  .service-area-feature-grid { grid-template-columns: minmax(0, 1fr); }
  .service-area-feature,
  .service-area-feature:nth-child(2) { border-right: 0; border-bottom: 1px solid var(--service-area-border); }
  .service-area-feature:last-child { border-bottom: 0; }
  .service-area-process { grid-template-columns: minmax(0, 1fr); gap: 12px; }
  .service-area-step:not(:last-child)::after { display: none; }
  .service-area-cta { grid-template-columns: minmax(0, 1fr); min-height: 360px; align-items: end; }
  .service-area-cta::after { background: linear-gradient(0deg, var(--service-area-cta-bg) 0%, color-mix(in srgb, var(--service-area-cta-bg) 92%, transparent) 52%, transparent 100%); }
  .service-area-cta-copy { padding: 26px 22px; }
  .service-area-cta-media { inset: 0 0 28% 0; }
}
@container (max-width: 460px) {
  .service-area-eyebrow { margin-bottom: 12px; font-size: 12px; }
  .service-area-actions { display: grid; grid-template-columns: minmax(0, 1fr); }
  .service-area-action { width: 100%; }
  .service-area-hero-media,
  .service-area-hero-media > img { min-height: 280px; }
  .service-area-hero-badge { right: 10px; padding: 10px; }
  .service-area-hero-badge--top { top: 10px; }
  .service-area-hero-badge--bottom { bottom: 10px; }
  .service-area-stat { grid-template-columns: minmax(0, 1fr); align-content: start; min-height: 126px; }
  .service-area-stat-icon { width: 36px; height: 36px; }
  .service-area-map,
  .service-area-map-image { min-height: 500px; }
  .service-area-map-label { font-size: 11px; }
  .service-area-card-copy { grid-template-columns: 20px minmax(0, 1fr); padding: 10px; }
  .service-area-card-title { font-size: 12px; }
  .service-area-cta-title { font-size: 25px; }
}
`;

const joinClassNames = (...classNames: Array<string | false | null | undefined>) =>
    classNames.filter(Boolean).join(' ');

const toCssSize = (value?: number | string) => {
    if (typeof value === 'number') return `${value}px`;
    return value;
};

const kebabCase = (value: string) =>
    value.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);

const colorVariableNames: Partial<Record<ServiceAreaColorToken, string>> = {
    background: 'bg',
    mapBackground: 'map-bg',
    ctaBackground: 'cta-bg',
};

function toColorStyles(colors?: ServiceAreaColors): ServiceAreaCssVars {
    if (!colors) return {};

    return Object.entries(colors).reduce<ServiceAreaCssVars>((style, [token, value]) => {
        if (value) {
            const variableName =
                colorVariableNames[token as ServiceAreaColorToken] || kebabCase(token);
            style[`--service-area-${variableName}`] = value;
        }
        return style;
    }, {});
}

function isIconToken(icon: unknown): icon is ServiceAreaIconToken {
    return typeof icon === 'string' && iconTokenSet.has(icon);
}

function renderIcon(icon: ServiceAreaIcon | undefined, className: string, size: number) {
    if (icon === undefined || icon === null || icon === false) return null;

    const iconProps = {
        size,
        strokeWidth: 2.15,
        className,
        'aria-hidden': true,
        focusable: false,
    };

    if (isIconToken(icon)) {
        const Icon = iconRegistry[icon];
        return <Icon {...iconProps} />;
    }

    if (React.isValidElement(icon)) {
        return React.cloneElement(icon as React.ReactElement<any>, {
            ...iconProps,
            ...((icon as React.ReactElement<any>).props || {}),
        });
    }

    if (typeof icon === 'function') {
        const Icon = icon as React.ElementType;
        return <Icon {...iconProps} />;
    }

    return <span className={className}>{icon}</span>;
}

function imageSourceValue(src?: ServiceAreaImageSource) {
    if (!src) return undefined;
    if (typeof src === 'string') return src;
    return src.src;
}

function normalizeImage(
    image?: ServiceAreaImage | string | false,
    fallbackAlt = '',
    fallbackLoading: 'eager' | 'lazy' = 'lazy',
): NormalizedImage | undefined {
    if (!image) return undefined;

    if (typeof image === 'string') {
        return {
            src: image,
            alt: fallbackAlt,
            loading: fallbackLoading,
            decoding: 'async',
            decorative: !fallbackAlt,
        };
    }

    const src = imageSourceValue(image.src) || image.url || image.original || image.thumbnail;
    if (!src) return undefined;

    const alt = image.decorative ? '' : image.alt || fallbackAlt;

    return {
        src,
        alt,
        title: image.title,
        width: image.width || (typeof image.src === 'object' ? image.src.width : undefined),
        height: image.height || (typeof image.src === 'object' ? image.src.height : undefined),
        objectPosition: image.objectPosition,
        loading: image.loading || fallbackLoading,
        decoding: image.decoding || 'async',
        decorative: image.decorative || !alt,
    };
}

function ImageElement({ image, className }: { image: NormalizedImage; className?: string }) {
    return (
        <img
            className={className}
            src={image.src}
            alt={image.alt}
            title={image.title}
            width={image.width}
            height={image.height}
            loading={image.loading}
            decoding={image.decoding}
            aria-hidden={image.decorative ? true : undefined}
            style={image.objectPosition ? { objectPosition: image.objectPosition } : undefined}
        />
    );
}

const humanizeId = (value: string) =>
    value
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (letter) => letter.toUpperCase());

const slugify = (value: string) =>
    value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

const lowerFirst = (value: string) => value.replace(/^./, (letter) => letter.toLowerCase());

function deriveServiceName(pagePath: string, suburb: string) {
    const pathSlug = pagePath.replace(/^\/+|\/+$/g, '');
    const suburbSlug = slugify(suburb);
    const serviceSlug = pathSlug.endsWith(`-${suburbSlug}`)
        ? pathSlug.slice(0, -(suburbSlug.length + 1))
        : pathSlug;

    return lowerFirst(humanizeId(serviceSlug || 'service'));
}

function textValue(value: React.ReactNode, fallback: string) {
    return typeof value === 'string' || typeof value === 'number' ? String(value) : fallback;
}

function formatPercent(value?: number) {
    return typeof value === 'number' && Number.isFinite(value) ? `${value}%` : '—';
}

function iconForId(id: string, kind: 'solution' | 'finish' | 'feature'): ServiceAreaIconToken {
    const normalized = id.toLowerCase();
    if (kind === 'finish') return 'palette';
    if (normalized.includes('apartment')) return 'building';
    if (normalized.includes('coastal') || normalized.includes('beach')) return 'waves';
    if (normalized.includes('renovation')) return 'tools';
    if (normalized.includes('drawer')) return 'drawers';
    if (normalized.includes('sliding') || normalized.includes('mirror')) return 'sliding';
    if (normalized.includes('hinged')) return 'wardrobe';
    if (normalized.includes('internal') || normalized.includes('carcass')) return 'layers';
    return kind === 'feature' ? 'sparkles' : 'wardrobe';
}

function resolveCatalogueEntries(
    ids: string[] | undefined,
    catalogue: Record<string, ServiceAreaCatalogueEntry> | undefined,
    kind: 'solution' | 'finish' | 'feature',
): ResolvedCatalogueEntry[] {
    return (ids || []).map((id) => {
        const item = catalogue?.[id] || {};
        return {
            ...item,
            id: item.id || id,
            label: item.label || item.title || item.name || humanizeId(id),
            icon: item.icon || iconForId(id, kind),
        };
    });
}

function ServiceAreaLink({
    action,
    LinkComponent,
}: {
    action: ServiceAreaAction;
    LinkComponent?: ServiceAreaLinkComponent;
}) {
    const className = joinClassNames(
        'service-area-action',
        `service-area-action--${action.variant || 'primary'}`,
    );
    const rel = action.rel || (action.target === '_blank' ? 'noopener noreferrer' : undefined);
    const content = (
        <>
            <span>{action.label}</span>
            {renderIcon('arrowRight', 'service-area-action-icon', 18)}
        </>
    );

    if (LinkComponent) {
        return (
            <LinkComponent
                href={action.href}
                className={className}
                aria-label={action.ariaLabel}
                title={action.title}
                target={action.target}
                rel={rel}
            >
                {content}
            </LinkComponent>
        );
    }

    return (
        <a
            href={action.href}
            className={className}
            aria-label={action.ariaLabel}
            title={action.title}
            target={action.target}
            rel={rel}
        >
            {content}
        </a>
    );
}

function HeroMedia({
    image,
    badges,
    fallbackLabel,
}: {
    image?: NormalizedImage;
    badges: ServiceAreaHeroBadge[];
    fallbackLabel: string;
}) {
    return (
        <div className="service-area-hero-media">
            {image ? (
                <ImageElement image={image} />
            ) : (
                <div className="service-area-media-fallback" role="img" aria-label={fallbackLabel}>
                    <div className="service-area-media-fallback-content">
                        {renderIcon('wardrobe', 'service-area-media-fallback-icon', 52)}
                        <span>{fallbackLabel}</span>
                    </div>
                </div>
            )}
            {badges.slice(0, 2).map((badge, index) => (
                <div
                    key={badge.id ?? index}
                    className={joinClassNames(
                        'service-area-hero-badge',
                        `service-area-hero-badge--${badge.position || (index ? 'bottom' : 'top')}`,
                    )}
                >
                    <span className="service-area-hero-badge-icon">
                        {renderIcon(badge.icon || (index ? 'badgeCheck' : 'ruler'), '', 18)}
                    </span>
                    <div>
                        <p className="service-area-hero-badge-title">{badge.title}</p>
                        {badge.description ? (
                            <p className="service-area-hero-badge-description">
                                {badge.description}
                            </p>
                        ) : null}
                    </div>
                </div>
            ))}
        </div>
    );
}

function MapPanel({ map, location }: { map: ServiceAreaMap; location: ServiceAreaLocation }) {
    const mapTitle = map.title || `${location.suburb} service area map`;
    const mapImage = normalizeImage(map.image, mapTitle);
    const detailImage = normalizeImage(map.detailImage, `Example service in ${location.suburb}`);
    const serviceLabel =
        map.serviceLabel ||
        `Service area: ${location.suburb}, ${location.state} ${location.postcode}`;
    const tags = (location.areaTags || []).filter(Boolean).slice(0, 4);

    return (
        <div className="service-area-map">
            {map.embedUrl ? (
                <GoogleServiceArea
                    googleDetails={{ googleServiceArea: map.embedUrl }}
                    title={mapTitle}
                    className="service-area-map-frame"
                    frameClassName="service-area-map-aspect"
                    iframeClassName="service-area-map-iframe"
                />
            ) : mapImage ? (
                <ImageElement image={mapImage} className="service-area-map-image" />
            ) : (
                <div className="service-area-map-fallback" role="img" aria-label={mapTitle}>
                    <div className="service-area-map-fallback-content">
                        {renderIcon('mapPin', '', 34)}
                        <strong>{location.suburb}</strong>
                        <span>Illustrative service-area context</span>
                    </div>
                </div>
            )}

            <div className="service-area-map-label">
                {renderIcon('mapPin', '', 14)}
                <span>{serviceLabel}</span>
            </div>

            {location.localGovernmentArea || location.region || tags.length ? (
                <aside className="service-area-map-detail" aria-label="Local area details">
                    <p className="service-area-map-detail-title">
                        {location.localGovernmentArea || `${location.suburb} area`}
                    </p>
                    <dl className="service-area-map-detail-list">
                        {location.localGovernmentArea ? (
                            <>
                                <dt>LGA</dt>
                                <dd>{location.localGovernmentArea}</dd>
                            </>
                        ) : null}
                        {location.region ? (
                            <>
                                <dt>Region</dt>
                                <dd>{location.region}</dd>
                            </>
                        ) : null}
                    </dl>
                    {tags.length ? (
                        <ul className="service-area-map-tags">
                            {tags.map((tag) => (
                                <li key={tag}>{tag}</li>
                            ))}
                        </ul>
                    ) : null}
                </aside>
            ) : null}

            {detailImage ? (
                <div className="service-area-map-thumbnail">
                    <ImageElement image={detailImage} />
                </div>
            ) : null}
        </div>
    );
}

function CatalogueCard({
    item,
    kind,
    LinkComponent,
}: {
    item: ResolvedCatalogueEntry;
    kind: 'solution' | 'finish';
    LinkComponent?: ServiceAreaLinkComponent;
}) {
    const image = normalizeImage(
        item.image,
        `${textValue(item.label, humanizeId(item.id))} ${kind}`,
    );
    const content = (
        <>
            <div className="service-area-card-media">
                {image ? (
                    <ImageElement image={image} />
                ) : (
                    renderIcon(item.icon || iconForId(item.id, kind), '', 38)
                )}
            </div>
            <div className="service-area-card-copy">
                {kind === 'solution' ? (
                    <span className="service-area-card-icon">
                        {renderIcon(item.icon || iconForId(item.id, kind), '', 20)}
                    </span>
                ) : null}
                <div>
                    <p className="service-area-card-title">{item.label}</p>
                    {item.description ? (
                        <p className="service-area-card-description">{item.description}</p>
                    ) : null}
                    {item.meta ? <p className="service-area-card-meta">{item.meta}</p> : null}
                </div>
            </div>
        </>
    );
    const className = item.href ? 'service-area-card-link' : 'service-area-card-static';

    return (
        <li className={joinClassNames('service-area-card', `service-area-card--${kind}`)}>
            {item.href && LinkComponent ? (
                <LinkComponent href={item.href} className={className}>
                    {content}
                </LinkComponent>
            ) : item.href ? (
                <a href={item.href} className={className}>
                    {content}
                </a>
            ) : (
                <div className={className}>{content}</div>
            )}
        </li>
    );
}

function CardSection({
    id,
    title,
    items,
    kind,
    LinkComponent,
}: {
    id: string;
    title: React.ReactNode;
    items: ResolvedCatalogueEntry[];
    kind: 'solution' | 'finish';
    LinkComponent?: ServiceAreaLinkComponent;
}) {
    if (!items.length) return null;

    const columns = Math.min(items.length, kind === 'solution' ? 6 : 5);

    return (
        <section className="service-area-section" aria-labelledby={id}>
            <div className="service-area-section-heading">
                <h2 className="service-area-section-title" id={id}>
                    {title}
                </h2>
            </div>
            <ul
                className="service-area-card-grid"
                style={
                    {
                        '--service-area-card-width': `calc((100% - ${(columns - 1) * 12}px) / ${columns})`,
                    } as ServiceAreaCssVars
                }
                aria-labelledby={id}
                tabIndex={items.length > 1 ? 0 : undefined}
            >
                {items.map((item) => (
                    <CatalogueCard
                        key={item.id}
                        item={item}
                        kind={kind}
                        LinkComponent={LinkComponent}
                    />
                ))}
            </ul>
        </section>
    );
}

function NearbyLink({
    area,
    LinkComponent,
}: {
    area: ServiceAreaNearbyArea;
    LinkComponent?: ServiceAreaLinkComponent;
}) {
    const content = (
        <>
            {renderIcon('mapPin', '', 13)}
            <span>{area.label}</span>
        </>
    );

    if (area.href && LinkComponent) {
        return (
            <LinkComponent
                href={area.href}
                className="service-area-nearby-link"
                aria-label={area.ariaLabel}
            >
                {content}
            </LinkComponent>
        );
    }

    if (area.href) {
        return (
            <a href={area.href} className="service-area-nearby-link" aria-label={area.ariaLabel}>
                {content}
            </a>
        );
    }

    return <span className="service-area-nearby-static">{content}</span>;
}

export default function ServiceArea({
    data,
    pagePath,
    serviceName,
    eyebrow,
    title,
    description,
    heroImage,
    heroBadges = false,
    primaryAction = false,
    secondaryAction = false,
    stats,
    insights,
    map = {},
    solutions,
    finishes,
    fitFeatures,
    process = false,
    nearbyAreas = false,
    cta = false,
    labels,
    LinkComponent,
    Link,
    headingLevel = 'h1',
    accentColor,
    accentColour,
    accentDarkColor,
    accentDarkColour,
    accentSoftColor,
    accentSoftColour,
    actionColor,
    actionColour,
    actionDarkColor,
    actionDarkColour,
    backgroundColor,
    backgroundColour,
    surfaceColor,
    surfaceColour,
    textColor,
    textColour,
    textSoftColor,
    textSoftColour,
    colors,
    colours,
    fontFamily,
    maxWidth,
    radius,
    id,
    className,
    style,
    ariaLabel,
}: ServiceAreaProps) {
    const entries = Object.entries(data.pages || {});
    const resolvedPath = pagePath || entries[0]?.[0];
    const page = resolvedPath ? data.pages[resolvedPath] : undefined;

    if (!page || !resolvedPath) {
        throw new Error(
            `ServiceArea could not resolve ${pagePath ? `page "${pagePath}"` : 'a page'} from the supplied data model.`,
        );
    }

    const location = page.location;
    const dwelling = page.areaProfile?.dwellingProfile;
    const structure = dwelling?.dwellingStructurePercent || {};
    const resolvedServiceName = serviceName || deriveServiceName(resolvedPath, location.suburb);
    const serviceLabel = humanizeId(resolvedServiceName);
    const Heading = headingLevel;
    const ResolvedLink = LinkComponent || Link;
    const resolvedTitle = title || `${serviceLabel} in ${location.suburb}`;
    const resolvedDescription =
        description ||
        `${serviceLabel} tailored to homes across ${location.suburb}${location.region ? ` and the wider ${location.region}` : ''}, informed by the area's local dwelling profile and renovation needs.`;
    const resolvedEyebrow = eyebrow || `${location.suburb} ${t.defaults.pageTypeLabel}`;
    const resolvedHeroImage = normalizeImage(
        heroImage,
        `${serviceLabel} designed for a home in ${location.suburb}`,
        'eager',
    );

    const defaultStats: ServiceAreaStat[] = [
        {
            id: 'postcode',
            label: t.defaults.postcodeLabel,
            value: location.postcode,
            description: `${location.suburb}, ${location.state}`,
            icon: 'mapPin',
        },
        {
            id: 'apartments',
            label: t.defaults.apartmentsLabel,
            value: formatPercent(structure.flatOrApartment),
            description: 'Flats or apartments',
            icon: 'building',
        },
        {
            id: 'houses',
            label: t.defaults.housesLabel,
            value: formatPercent(structure.separateHouse),
            description: 'Separate houses',
            icon: 'house',
        },
        {
            id: 'townhouses',
            label: t.defaults.townhousesLabel,
            value: formatPercent(structure.semiDetachedRowTerraceTownhouse),
            description: 'Semi-detached / row / terrace',
            icon: 'layers',
        },
        {
            id: 'snapshot',
            label: t.defaults.snapshotLabel,
            value: dwelling?.dataYear || '—',
            description: dwelling?.dataYear ? `ABS Census ${dwelling.dataYear}` : undefined,
            icon: 'calendar',
        },
        {
            id: 'region',
            label: t.defaults.regionLabel,
            value: location.region || location.localGovernmentArea || location.state,
            description: location.localGovernmentArea,
            icon: 'badgeCheck',
        },
    ];
    const resolvedStats = stats === false ? [] : stats || defaultStats;

    const defaultInsights: ServiceAreaInsight[] = [
        ...(dwelling?.primaryPattern
            ? [
                  {
                      id: 'primary-pattern',
                      text: dwelling.primaryPattern,
                      icon: 'checkCircle' as const,
                  },
              ]
            : []),
        ...(typeof structure.separateHouse === 'number'
            ? [
                  {
                      id: 'separate-house',
                      text: `${formatPercent(structure.separateHouse)} of occupied private dwellings were separate houses${dwelling?.dataYear ? ` (${dwelling.dataYear})` : ''}.`,
                      icon: 'checkCircle' as const,
                  },
              ]
            : []),
        ...(typeof structure.semiDetachedRowTerraceTownhouse === 'number'
            ? [
                  {
                      id: 'townhouse',
                      text: `${formatPercent(structure.semiDetachedRowTerraceTownhouse)} were semi-detached, row, terrace or townhouse dwellings.`,
                      icon: 'checkCircle' as const,
                  },
              ]
            : []),
    ];
    const resolvedInsights = insights === false ? [] : insights || defaultInsights;

    const catalogue = data.catalogue || {};
    const defaultSolutions = resolveCatalogueEntries(
        page.recommendations?.solutionIds,
        catalogue.solutions,
        'solution',
    );
    const defaultFinishes = resolveCatalogueEntries(
        page.recommendations?.finishIds,
        catalogue.finishes,
        'finish',
    );
    const resolvedSolutions =
        solutions === false
            ? []
            : solutions
              ? solutions.map((item, index) => ({
                    ...item,
                    id: item.id || `solution-${index}`,
                    label: item.label || item.title || item.name || `Solution ${index + 1}`,
                }))
              : defaultSolutions;
    const resolvedFinishes =
        finishes === false
            ? []
            : finishes
              ? finishes.map((item, index) => ({
                    ...item,
                    id: item.id || `finish-${index}`,
                    label: item.label || item.title || item.name || `Finish ${index + 1}`,
                }))
              : defaultFinishes;

    const recommendedFeatures = resolveCatalogueEntries(
        page.recommendations?.featureIds,
        catalogue.features,
        'feature',
    ).map<ServiceAreaFeature>((item) => ({
        id: item.id,
        title: item.label,
        description: item.description,
        icon: item.icon,
    }));
    const tagFeatures = (location.areaTags || []).slice(0, 4).map<ServiceAreaFeature>((tag) => ({
        id: tag,
        title: humanizeId(tag),
        icon: iconForId(tag, 'feature'),
    }));
    const resolvedFitFeatures =
        fitFeatures === false
            ? []
            : fitFeatures || (recommendedFeatures.length ? recommendedFeatures : tagFeatures);

    const resolvedColors = { ...colors, ...colours };

    const rootStyle: ServiceAreaCssVars = {
        ...toColorStyles(resolvedColors),
        '--service-area-bg': backgroundColour || backgroundColor || resolvedColors.background,
        '--service-area-surface': surfaceColour || surfaceColor || resolvedColors.surface,
        '--service-area-text': textColour || textColor || resolvedColors.text,
        '--service-area-text-soft': textSoftColour || textSoftColor || resolvedColors.textSoft,
        '--service-area-accent': accentColour || accentColor || resolvedColors.accent,
        '--service-area-accent-dark':
            accentDarkColour || accentDarkColor || resolvedColors.accentDark,
        '--service-area-accent-soft':
            accentSoftColour || accentSoftColor || resolvedColors.accentSoft,
        '--service-area-action': actionColour || actionColor || resolvedColors.action,
        '--service-area-action-dark':
            actionDarkColour || actionDarkColor || resolvedColors.actionDark,
        '--service-area-font': fontFamily,
        '--service-area-max-width': toCssSize(maxWidth ?? t.sizes.maxWidth),
        '--service-area-radius': toCssSize(radius ?? t.radius.card),
        ...style,
    };

    const schemaArea = `${location.suburb}, ${location.state} ${location.postcode}`;
    const resolvedMap = map || {};
    const resolvedHeroBadges = heroBadges || [];
    const resolvedProcess = process || [];
    const resolvedNearbyAreas = nearbyAreas || [];
    const resolvedCtaImage = cta ? normalizeImage(cta.image, '') : undefined;

    return (
        <article
            id={id}
            className={joinClassNames('service-area', className)}
            style={rootStyle}
            aria-label={ariaLabel || `${serviceLabel} in ${location.suburb}`}
            itemScope
            itemType="https://schema.org/Service"
        >
            <style>{serviceAreaCss}</style>
            <meta itemProp="areaServed" content={schemaArea} />
            <div className="service-area-shell">
                <header className="service-area-hero">
                    <div className="service-area-hero-copy">
                        {resolvedEyebrow ? (
                            <p className="service-area-eyebrow">{resolvedEyebrow}</p>
                        ) : null}
                        <Heading className="service-area-title" itemProp="name">
                            {resolvedTitle}
                        </Heading>
                        {resolvedDescription ? (
                            <p className="service-area-description" itemProp="description">
                                {resolvedDescription}
                            </p>
                        ) : null}
                        {primaryAction || secondaryAction ? (
                            <div className="service-area-actions">
                                {primaryAction ? (
                                    <ServiceAreaLink
                                        action={{ ...primaryAction, variant: 'primary' }}
                                        LinkComponent={ResolvedLink}
                                    />
                                ) : null}
                                {secondaryAction ? (
                                    <ServiceAreaLink
                                        action={{ ...secondaryAction, variant: 'secondary' }}
                                        LinkComponent={ResolvedLink}
                                    />
                                ) : null}
                            </div>
                        ) : null}
                    </div>

                    <HeroMedia
                        image={resolvedHeroImage}
                        badges={resolvedHeroBadges}
                        fallbackLabel={`${serviceLabel} in ${location.suburb}`}
                    />
                </header>

                {resolvedStats.length ? (
                    <dl className="service-area-stats" aria-label={`${location.suburb} area facts`}>
                        {resolvedStats.map((stat, index) => (
                            <div className="service-area-stat" key={stat.id ?? index}>
                                <span className="service-area-stat-icon">
                                    {renderIcon(stat.icon || 'checkCircle', '', 20)}
                                </span>
                                <div className="service-area-stat-copy">
                                    <dt className="service-area-stat-label">{stat.label}</dt>
                                    <dd className="service-area-stat-value">{stat.value}</dd>
                                    {stat.description ? (
                                        <dd className="service-area-stat-description">
                                            {stat.description}
                                        </dd>
                                    ) : null}
                                </div>
                            </div>
                        ))}
                    </dl>
                ) : null}

                {resolvedInsights.length || map !== false ? (
                    <section
                        className="service-area-insights"
                        aria-labelledby="service-area-insights-title"
                    >
                        <div className="service-area-insight-copy">
                            <div>
                                <p className="service-area-section-eyebrow">
                                    {labels?.insightsEyebrow || t.defaults.insightsEyebrow}
                                </p>
                                <h2
                                    className="service-area-section-title"
                                    id="service-area-insights-title"
                                >
                                    {labels?.insightsTitle ||
                                        `What the data says about ${location.suburb}`}
                                </h2>
                                {resolvedInsights.length ? (
                                    <ul className="service-area-insight-list">
                                        {resolvedInsights.map((insight, index) => (
                                            <li
                                                className="service-area-insight-item"
                                                key={insight.id ?? index}
                                            >
                                                {renderIcon(insight.icon || 'checkCircle', '', 16)}
                                                <span>{insight.text}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                            </div>
                        </div>
                        {map !== false ? <MapPanel map={resolvedMap} location={location} /> : null}
                    </section>
                ) : null}

                <CardSection
                    id="service-area-solutions-title"
                    title={
                        labels?.solutionsTitle ||
                        `${t.defaults.solutionsTitle} for ${location.suburb}`
                    }
                    items={resolvedSolutions}
                    kind="solution"
                    LinkComponent={ResolvedLink}
                />

                <CardSection
                    id="service-area-finishes-title"
                    title={
                        labels?.finishesTitle || `${t.defaults.finishesTitle} in ${location.suburb}`
                    }
                    items={resolvedFinishes}
                    kind="finish"
                    LinkComponent={ResolvedLink}
                />

                {resolvedFitFeatures.length ? (
                    <section
                        className="service-area-section"
                        aria-labelledby="service-area-fit-title"
                    >
                        <div className="service-area-section-heading">
                            <h2 className="service-area-section-title" id="service-area-fit-title">
                                {labels?.fitTitle || `Built for the way ${location.suburb} lives`}
                            </h2>
                        </div>
                        <ul className="service-area-feature-grid">
                            {resolvedFitFeatures.slice(0, 4).map((feature, index) => (
                                <li className="service-area-feature" key={feature.id ?? index}>
                                    <span className="service-area-feature-icon">
                                        {renderIcon(feature.icon || 'sparkles', '', 22)}
                                    </span>
                                    <div>
                                        <p className="service-area-feature-title">
                                            {feature.title}
                                        </p>
                                        {feature.description ? (
                                            <p className="service-area-feature-description">
                                                {feature.description}
                                            </p>
                                        ) : null}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </section>
                ) : null}

                {resolvedProcess.length ? (
                    <section
                        className="service-area-section"
                        aria-labelledby="service-area-process-title"
                    >
                        <div className="service-area-section-heading">
                            <h2
                                className="service-area-section-title"
                                id="service-area-process-title"
                            >
                                {labels?.processTitle || t.defaults.processTitle}
                            </h2>
                        </div>
                        <ol className="service-area-process">
                            {resolvedProcess.slice(0, 3).map((step, index) => (
                                <li className="service-area-step" key={step.id ?? index}>
                                    <div className="service-area-step-icon-wrap">
                                        <span className="service-area-step-icon">
                                            {renderIcon(
                                                step.icon ||
                                                    (index === 0
                                                        ? 'users'
                                                        : index === 1
                                                          ? 'ruler'
                                                          : 'tools'),
                                                '',
                                                23,
                                            )}
                                        </span>
                                        <span className="service-area-step-number">
                                            {index + 1}
                                        </span>
                                    </div>
                                    <div>
                                        <p className="service-area-step-title">{step.title}</p>
                                        {step.description ? (
                                            <p className="service-area-step-description">
                                                {step.description}
                                            </p>
                                        ) : null}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </section>
                ) : null}

                {resolvedNearbyAreas.length ? (
                    <section
                        className="service-area-section"
                        aria-labelledby="service-area-nearby-title"
                    >
                        <div className="service-area-section-heading">
                            <h2
                                className="service-area-section-title"
                                id="service-area-nearby-title"
                            >
                                {labels?.nearbyAreasTitle || t.defaults.nearbyAreasTitle}
                            </h2>
                        </div>
                        <ul className="service-area-nearby">
                            {resolvedNearbyAreas.map((area, index) => (
                                <li key={area.id ?? index}>
                                    <NearbyLink area={area} LinkComponent={ResolvedLink} />
                                </li>
                            ))}
                        </ul>
                    </section>
                ) : null}

                {cta ? (
                    <aside className="service-area-cta" aria-label="Service enquiry">
                        {resolvedCtaImage ? (
                            <div className="service-area-cta-media" aria-hidden="true">
                                <ImageElement image={resolvedCtaImage} />
                            </div>
                        ) : null}
                        <div className="service-area-cta-copy">
                            <h2 className="service-area-cta-title">{cta.title}</h2>
                            {cta.description ? (
                                <p className="service-area-cta-description">{cta.description}</p>
                            ) : null}
                            <div className="service-area-actions">
                                {cta.secondaryAction ? (
                                    <ServiceAreaLink
                                        action={{ ...cta.secondaryAction, variant: 'secondary' }}
                                        LinkComponent={ResolvedLink}
                                    />
                                ) : null}
                                <ServiceAreaLink
                                    action={{ ...cta.primaryAction, variant: 'primary' }}
                                    LinkComponent={ResolvedLink}
                                />
                            </div>
                        </div>
                    </aside>
                ) : null}
            </div>
        </article>
    );
}

export { serviceAreaIconTokens, serviceAreaTokens } from './ServiceArea.tokens';
export type { ServiceAreaColorToken, ServiceAreaIconToken } from './ServiceArea.tokens';
